import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../homepage/components/Navbar.jsx';
import eclipseerlogo from './eclipseer-logo.png';
import { authAPI } from '../../services/apiClient';
import styles from './ForgotPassword.module.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [linkValid, setLinkValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      setError('');
      if (!token) {
        setError('Reset link is missing. Please request a new password reset link.');
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.validateResetPasswordLink(token);
        setEmail(response?.data?.email || '');
        setExpiresAt(response?.data?.expires_at || '');
        setLinkValid(true);
      } catch (err) {
        setLinkValid(false);
        setError(
          err?.response?.data?.error ||
          err?.message ||
          'Invalid or expired password reset link.'
        );
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAPI.resetPasswordWithToken({
        token,
        new_password: newPassword
      });
      setSuccess(response?.data?.message || 'Password has been reset successfully.');
      setNewPassword('');
      setConfirmPassword('');
      setLinkValid(false);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Could not reset password. Please request a new link.';
      setError(msg);
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('used') || msg.toLowerCase().includes('invalid')) {
        setLinkValid(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar currentPage="home" setPage={() => {}} />
      <div className={styles.main}>
        <section className={styles.leftPanel}>
          <img src={eclipseerlogo} alt="Eclipseer Logo" className={styles.logo} />
          <h1 className={styles.panelTitle}>Set New Password</h1>
          <p className={styles.panelText}>
            This link is valid for one hour and only for the requested account email.
            After expiry, generate a new reset link from the login page.
          </p>
        </section>

        <section className={styles.rightPanel}>
          <h2 className={styles.heading}>Update Password</h2>
          <p className={styles.subheading}>
            {email ? `Account: ${email}` : 'Enter and confirm your new password to continue.'}
          </p>

          {loading && <div className={styles.note}>Validating reset link...</div>}
          {error && <div className={styles.messageError}>{error}</div>}
          {success && <div className={styles.messageSuccess}>{success}</div>}

          {!loading && linkValid && !success && (
            <form onSubmit={handleSubmit} className={styles.form}>
              <label htmlFor="newPassword" className={styles.label}>New Password</label>
              <input
                id="newPassword"
                type="password"
                className={styles.input}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                autoComplete="new-password"
                required
                disabled={submitting}
              />

              <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                className={styles.input}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                required
                disabled={submitting}
              />

              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Password'}
                </button>

                <Link to="/login" className={styles.secondaryButton} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                  Back to Login
                </Link>
              </div>
            </form>
          )}

          {expiresAt && !success && (
            <p className={styles.note}>This link expires on {new Date(expiresAt).toLocaleString()}.</p>
          )}

          {(success || error) && (
            <p className={styles.note}>
              Need a fresh link? <Link to="/forgot-password" className={styles.link}>Request password reset</Link>
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default ResetPassword;
